import { useEffect, useRef, useState, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface Platform {
  x: number;
  y: number;
  width: number;
  type: 'padbox' | 'hing3d' | 'gen5x' | 'basic';
  moving?: boolean;
  moveDirection?: number;
  moveRange?: number;
  originalX?: number;
}

interface Collectible {
  x: number;
  y: number;
  collected: boolean;
  type: 'sanwa-red' | 'sanwa-purple' | 'sanwa-blue' | 'sanwa-yellow' | 'usb-cable';
}

interface Enemy {
  x: number;
  y: number;
  velocityY: number;
  type: 'cherry-mx' | 'gateron';
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

interface Player {
  x: number;
  y: number;
  width: number;
  height: number;
  velocityY: number;
  velocityX: number;
  isJumping: boolean;
  jumpCount: number;
  maxJumps: number;
  invincible: boolean;
  invincibleTimer: number;
  slowMotion: boolean;
  slowMotionTimer: number;
  grappling: boolean;
  grappleX: number;
  grappleY: number;
  grappleTarget: { x: number; y: number } | null;
  facingRight: boolean;
  frameIndex: number;
}

type GameState = 'menu' | 'playing' | 'gameover';

// =============================================================================
// CONSTANTS
// =============================================================================

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;
const GRAVITY = 0.6;
const JUMP_STRENGTH = -13;
const PLAYER_SPEED = 5;
const PLATFORM_SPEED = 3;
const DISCOUNT_CODE = 'PADBOXPRO10';

// =============================================================================
// SOUND MANAGER (Web Audio API)
// =============================================================================

class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled = true;

  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  playJump() {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.frequency.setValueAtTime(400, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, this.ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.1);
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + 0.1);
  }

  playCollect() {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(880, this.ctx.currentTime);
    osc.frequency.setValueAtTime(1100, this.ctx.currentTime + 0.05);
    osc.frequency.setValueAtTime(1320, this.ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.2, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.15);
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + 0.15);
  }

  playLand() {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(150, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, this.ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.4, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.1);
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + 0.1);
  }

  playPowerUp() {
    if (!this.enabled || !this.ctx) return;
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();
      osc.connect(gain);
      gain.connect(this.ctx!.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, this.ctx!.currentTime + i * 0.08);
      gain.gain.setValueAtTime(0.2, this.ctx!.currentTime + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.01, this.ctx!.currentTime + i * 0.08 + 0.1);
      osc.start(this.ctx!.currentTime + i * 0.08);
      osc.stop(this.ctx!.currentTime + i * 0.08 + 0.1);
    });
  }

  playGameOver() {
    if (!this.enabled || !this.ctx) return;
    const notes = [400, 350, 300, 200];
    notes.forEach((freq, i) => {
      const osc = this.ctx!.createOscillator();
      const gain = this.ctx!.createGain();
      osc.connect(gain);
      gain.connect(this.ctx!.destination);
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, this.ctx!.currentTime + i * 0.15);
      gain.gain.setValueAtTime(0.3, this.ctx!.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.01, this.ctx!.currentTime + i * 0.15 + 0.2);
      osc.start(this.ctx!.currentTime + i * 0.15);
      osc.stop(this.ctx!.currentTime + i * 0.15 + 0.2);
    });
  }

  playCombo() {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, this.ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.25, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.2);
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + 0.2);
  }

  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  isEnabled() {
    return this.enabled;
  }
}

const soundManager = new SoundManager();

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const SkuValidationModal = ({ onClose }: { onClose: () => void }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [gameState, setGameState] = useState<GameState>('menu');
  const [score, setScore] = useState(0);
  const [, setCombo] = useState(0);
  const [showDiscountCode, setShowDiscountCode] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('arkoddRunnerHighScore') || '0');
  });

  const gameLoopRef = useRef<number>();
  const keysPressed = useRef<Set<string>>(new Set());
  const scoreRef = useRef(0);
  const comboRef = useRef(0);
  const lastPlatformLandedRef = useRef<Platform | null>(null);

  // Game state refs
  const playerRef = useRef<Player>({
    x: 100,
    y: 300,
    width: 40,
    height: 40,
    velocityY: 0,
    velocityX: 0,
    isJumping: false,
    jumpCount: 0,
    maxJumps: 2,
    invincible: false,
    invincibleTimer: 0,
    slowMotion: false,
    slowMotionTimer: 0,
    grappling: false,
    grappleX: 0,
    grappleY: 0,
    grappleTarget: null,
    facingRight: true,
    frameIndex: 0
  });

  const platformsRef = useRef<Platform[]>([]);
  const collectiblesRef = useRef<Collectible[]>([]);
  const enemiesRef = useRef<Enemy[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const scrollOffsetRef = useRef(0);
  const gameSpeedRef = useRef(PLATFORM_SPEED);
  const frameCountRef = useRef(0);
  const screenShakeRef = useRef(0);

  // =============================================================================
  // PARTICLE SYSTEM
  // =============================================================================

  const spawnParticles = (x: number, y: number, color: string, count: number, spread: number = 5) => {
    for (let i = 0; i < count; i++) {
      particlesRef.current.push({
        x,
        y,
        vx: (Math.random() - 0.5) * spread,
        vy: (Math.random() - 0.5) * spread - 2,
        life: 30 + Math.random() * 20,
        maxLife: 50,
        color,
        size: 3 + Math.random() * 4
      });
    }
  };

  // =============================================================================
  // INITIALIZE GAME
  // =============================================================================

  const initGame = useCallback(() => {
    soundManager.init();

    playerRef.current = {
      x: 100,
      y: 300,
      width: 40,
      height: 40,
      velocityY: 0,
      velocityX: 0,
      isJumping: false,
      jumpCount: 0,
      maxJumps: 2,
      invincible: false,
      invincibleTimer: 0,
      slowMotion: false,
      slowMotionTimer: 0,
      grappling: false,
      grappleX: 0,
      grappleY: 0,
      grappleTarget: null,
      facingRight: true,
      frameIndex: 0
    };

    platformsRef.current = [];
    collectiblesRef.current = [];
    enemiesRef.current = [];
    particlesRef.current = [];
    scrollOffsetRef.current = 0;
    gameSpeedRef.current = PLATFORM_SPEED;
    frameCountRef.current = 0;
    scoreRef.current = 0;
    comboRef.current = 0;
    lastPlatformLandedRef.current = null;
    setScore(0);
    setCombo(0);
    setShowDiscountCode(false);

    // Create starting platforms
    const types: Platform['type'][] = ['padbox', 'hing3d', 'gen5x', 'basic'];
    for (let i = 0; i < 15; i++) {
      const isMoving = i > 3 && Math.random() > 0.7;
      platformsRef.current.push({
        x: i * 150 + (i === 0 ? 0 : Math.random() * 80 - 40),
        y: 450 + Math.random() * 100 - 50,
        width: 90 + Math.random() * 40,
        type: types[Math.floor(Math.random() * types.length)],
        moving: isMoving,
        moveDirection: 1,
        moveRange: 50,
        originalX: 0
      });
      if (isMoving) {
        platformsRef.current[platformsRef.current.length - 1].originalX =
          platformsRef.current[platformsRef.current.length - 1].y;
      }
    }
  }, []);

  // =============================================================================
  // DRAWING FUNCTIONS
  // =============================================================================

  // Draw PadBox character (pixel art)
  const drawPlayer = (ctx: CanvasRenderingContext2D, player: Player) => {
    const px = 4; // pixel size

    // PadBox fightstick sprite (10x10)
    const sprite = [
      [0,0,2,2,2,2,2,2,0,0],
      [0,2,2,2,2,2,2,2,2,0],
      [2,2,1,2,2,2,2,1,2,2],
      [2,2,2,2,2,2,2,2,2,2],
      [2,3,3,2,4,4,4,4,2,2],
      [2,3,3,2,4,4,4,4,2,2],
      [2,2,2,2,4,4,4,4,2,2],
      [2,2,2,2,2,2,2,2,2,2],
      [0,2,2,2,2,2,2,2,2,0],
      [0,0,5,5,0,0,5,5,0,0],
    ];

    // Colors: 0=transparent, 1=screen/eye, 2=body, 3=joystick, 4=buttons, 5=feet
    const baseColors = ['transparent', '#00ffff', '#1a1a2e', '#ff0000', '#8b5cf6', '#333333'];
    const invincibleColors = ['transparent', '#ffff00', '#ffd700', '#ff0000', '#ff69b4', '#ffa500'];

    const colors = player.invincible && Math.floor(frameCountRef.current / 4) % 2 === 0
      ? invincibleColors
      : baseColors;

    ctx.save();

    // Flip if facing left
    if (!player.facingRight) {
      ctx.translate(player.x + player.width, player.y);
      ctx.scale(-1, 1);
      ctx.translate(-player.x, -player.y);
    }

    sprite.forEach((row, y) => {
      row.forEach((pixel, x) => {
        if (pixel !== 0) {
          ctx.fillStyle = colors[pixel];
          ctx.fillRect(
            player.x + x * px,
            player.y + y * px,
            px,
            px
          );
        }
      });
    });

    ctx.restore();

    // Draw grapple line
    if (player.grappling && player.grappleTarget) {
      ctx.strokeStyle = '#ffff00';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(player.x + player.width / 2, player.y + player.height / 2);
      ctx.lineTo(player.grappleTarget.x, player.grappleTarget.y);
      ctx.stroke();
    }
  };

  // Draw platform (Arkodd products)
  const drawPlatform = (ctx: CanvasRenderingContext2D, platform: Platform) => {
    const colors = {
      padbox: { main: '#8b5cf6', accent: '#a78bfa', keys: '#1a1a2e' },
      hing3d: { main: '#3b82f6', accent: '#60a5fa', keys: '#1e3a5f' },
      gen5x: { main: '#10b981', accent: '#34d399', keys: '#064e3b' },
      basic: { main: '#6b7280', accent: '#9ca3af', keys: '#374151' }
    };

    const c = colors[platform.type];

    // Main body with gradient effect
    ctx.fillStyle = c.main;
    ctx.fillRect(platform.x, platform.y, platform.width, 24);

    // Top highlight
    ctx.fillStyle = c.accent;
    ctx.fillRect(platform.x, platform.y, platform.width, 6);

    // Draw arcade buttons
    const buttonSize = 10;
    const buttonGap = 14;
    const numButtons = Math.floor((platform.width - 20) / buttonGap);

    for (let i = 0; i < numButtons; i++) {
      // Button base
      ctx.fillStyle = c.keys;
      ctx.beginPath();
      ctx.arc(
        platform.x + 12 + i * buttonGap,
        platform.y + 14,
        buttonSize / 2,
        0,
        Math.PI * 2
      );
      ctx.fill();

      // Button highlight
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.beginPath();
      ctx.arc(
        platform.x + 11 + i * buttonGap,
        platform.y + 13,
        buttonSize / 4,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    // Product label
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 8px monospace';
    ctx.fillText(platform.type.toUpperCase(), platform.x + 4, platform.y + 22);
  };

  // Draw collectible (Sanwa buttons)
  const drawCollectible = (ctx: CanvasRenderingContext2D, item: Collectible) => {
    if (item.collected) return;

    const colors: Record<string, { main: string; glow: string }> = {
      'sanwa-red': { main: '#ef4444', glow: '#fca5a5' },
      'sanwa-purple': { main: '#8b5cf6', glow: '#c4b5fd' },
      'sanwa-blue': { main: '#3b82f6', glow: '#93c5fd' },
      'sanwa-yellow': { main: '#fbbf24', glow: '#fde68a' },
      'usb-cable': { main: '#10b981', glow: '#6ee7b7' }
    };

    const c = colors[item.type];
    const bob = Math.sin(frameCountRef.current * 0.1) * 3;

    // Glow effect
    ctx.shadowColor = c.glow;
    ctx.shadowBlur = 15;

    if (item.type === 'usb-cable') {
      // USB Cable collectible
      ctx.fillStyle = c.main;
      ctx.fillRect(item.x, item.y + bob, 20, 8);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(item.x + 2, item.y + bob + 2, 6, 4);
      ctx.fillStyle = '#333';
      ctx.fillRect(item.x + 10, item.y + bob + 1, 8, 6);
    } else {
      // Sanwa button
      ctx.fillStyle = c.main;
      ctx.beginPath();
      ctx.arc(item.x + 10, item.y + 10 + bob, 12, 0, Math.PI * 2);
      ctx.fill();

      // Inner circle
      ctx.fillStyle = c.glow;
      ctx.beginPath();
      ctx.arc(item.x + 8, item.y + 8 + bob, 5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.shadowBlur = 0;
  };

  // Draw enemy (Cherry MX switches falling)
  const drawEnemy = (ctx: CanvasRenderingContext2D, enemy: Enemy) => {
    const colors = enemy.type === 'cherry-mx'
      ? { body: '#dc2626', stem: '#991b1b' }
      : { body: '#fbbf24', stem: '#b45309' };

    // Switch housing
    ctx.fillStyle = colors.body;
    ctx.fillRect(enemy.x, enemy.y, 20, 20);

    // Stem
    ctx.fillStyle = colors.stem;
    ctx.fillRect(enemy.x + 6, enemy.y + 4, 8, 12);

    // Warning indicator
    ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.moveTo(enemy.x + 10, enemy.y + 24);
    ctx.lineTo(enemy.x + 5, enemy.y + 34);
    ctx.lineTo(enemy.x + 15, enemy.y + 34);
    ctx.closePath();
    ctx.fill();
  };

  // Draw particles
  const drawParticles = (ctx: CanvasRenderingContext2D) => {
    particlesRef.current.forEach(p => {
      const alpha = p.life / p.maxLife;
      ctx.fillStyle = p.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');
      ctx.fillRect(p.x, p.y, p.size * alpha, p.size * alpha);
    });
  };

  // Draw CRT effect
  const drawCRTEffect = (ctx: CanvasRenderingContext2D) => {
    // Scanlines
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    for (let y = 0; y < CANVAS_HEIGHT; y += 4) {
      ctx.fillRect(0, y, CANVAS_WIDTH, 2);
    }

    // Vignette
    const gradient = ctx.createRadialGradient(
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 0,
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_WIDTH * 0.7
    );
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.4)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Random flicker
    if (Math.random() > 0.98) {
      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }
  };

  // Draw speed lines
  const drawSpeedLines = (ctx: CanvasRenderingContext2D, speed: number) => {
    if (speed < 4) return;

    ctx.strokeStyle = `rgba(255, 255, 255, ${Math.min((speed - 4) * 0.1, 0.3)})`;
    ctx.lineWidth = 2;

    for (let i = 0; i < speed * 2; i++) {
      const y = (i * 97 + frameCountRef.current * 7) % CANVAS_HEIGHT;
      const length = 20 + speed * 5;
      ctx.beginPath();
      ctx.moveTo(CANVAS_WIDTH, y);
      ctx.lineTo(CANVAS_WIDTH - length, y);
      ctx.stroke();
    }
  };

  // =============================================================================
  // GAME LOOP
  // =============================================================================

  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const player = playerRef.current;
    const timeScale = player.slowMotion ? 0.4 : 1;

    frameCountRef.current++;

    // Update timers
    if (player.invincible) {
      player.invincibleTimer -= timeScale;
      if (player.invincibleTimer <= 0) {
        player.invincible = false;
      }
    }
    if (player.slowMotion) {
      player.slowMotionTimer -= 1;
      if (player.slowMotionTimer <= 0) {
        player.slowMotion = false;
      }
    }

    // Screen shake decay
    if (screenShakeRef.current > 0) {
      screenShakeRef.current *= 0.9;
      if (screenShakeRef.current < 0.5) screenShakeRef.current = 0;
    }

    // Apply screen shake
    ctx.save();
    if (screenShakeRef.current > 0) {
      ctx.translate(
        (Math.random() - 0.5) * screenShakeRef.current,
        (Math.random() - 0.5) * screenShakeRef.current
      );
    }

    // Clear canvas with gradient background
    const bgGradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    bgGradient.addColorStop(0, '#0a0a1a');
    bgGradient.addColorStop(1, '#1a0a2e');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw stars background
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < 80; i++) {
      const x = (i * 123 + scrollOffsetRef.current * 0.3) % CANVAS_WIDTH;
      const y = (i * 456) % CANVAS_HEIGHT;
      const size = (i % 3) + 1;
      const twinkle = Math.sin(frameCountRef.current * 0.05 + i) > 0.8 ? 2 : 0;
      ctx.fillRect(x, y, size + twinkle, size + twinkle);
    }

    // Draw speed lines
    drawSpeedLines(ctx, gameSpeedRef.current);

    // Apply gravity
    player.velocityY += GRAVITY * timeScale;
    player.y += player.velocityY * timeScale;

    // Horizontal movement
    if (keysPressed.current.has('ArrowLeft') || keysPressed.current.has('KeyA')) {
      player.x -= PLAYER_SPEED * timeScale;
      player.facingRight = false;
    }
    if (keysPressed.current.has('ArrowRight') || keysPressed.current.has('KeyD')) {
      player.x += PLAYER_SPEED * timeScale;
      player.facingRight = true;
    }

    // Keep player in bounds
    player.x = Math.max(0, Math.min(CANVAS_WIDTH - player.width, player.x));

    // Grapple physics
    if (player.grappling && player.grappleTarget) {
      const dx = player.grappleTarget.x - (player.x + player.width / 2);
      const dy = player.grappleTarget.y - (player.y + player.height / 2);
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 20) {
        player.grappling = false;
        player.grappleTarget = null;
        player.velocityY = -10;
      } else {
        player.x += (dx / dist) * 8 * timeScale;
        player.y += (dy / dist) * 8 * timeScale;
        player.velocityY = 0;
      }
    }

    // Platform collision & movement
    let onPlatform = false;
    platformsRef.current.forEach(platform => {
      // Move moving platforms
      if (platform.moving && platform.originalX !== undefined) {
        platform.y = platform.originalX + Math.sin(frameCountRef.current * 0.03) * platform.moveRange!;
      }

      const platformX = platform.x - scrollOffsetRef.current;

      if (
        player.x + player.width > platformX &&
        player.x < platformX + platform.width &&
        player.y + player.height > platform.y &&
        player.y + player.height < platform.y + 30 &&
        player.velocityY > 0
      ) {
        player.y = platform.y - player.height;

        // Check if this is a new platform for combo
        if (lastPlatformLandedRef.current !== platform) {
          if (lastPlatformLandedRef.current !== null && !player.isJumping) {
            comboRef.current++;
            setCombo(comboRef.current);

            if (comboRef.current >= 3) {
              const bonus = comboRef.current * 5;
              scoreRef.current += bonus;
              setScore(scoreRef.current);
              spawnParticles(player.x + player.width / 2, player.y, '#fbbf24', 10, 8);
              soundManager.playCombo();
            }
          }
          lastPlatformLandedRef.current = platform;
        }

        if (player.velocityY > 8) {
          screenShakeRef.current = player.velocityY * 0.8;
          soundManager.playLand();
        }

        player.velocityY = 0;
        player.isJumping = false;
        player.jumpCount = 0;
        onPlatform = true;

        // Spawn landing particles
        spawnParticles(player.x + player.width / 2, player.y + player.height, '#ffffff', 5, 3);
      }
    });

    // Reset combo if in air for too long
    if (!onPlatform && player.velocityY > 5) {
      comboRef.current = 0;
      setCombo(0);
    }

    // Collectible collision
    collectiblesRef.current.forEach(item => {
      if (item.collected) return;

      const itemX = item.x - scrollOffsetRef.current;
      if (
        player.x + player.width > itemX &&
        player.x < itemX + 24 &&
        player.y + player.height > item.y &&
        player.y < item.y + 24
      ) {
        item.collected = true;
        soundManager.playCollect();

        switch (item.type) {
          case 'sanwa-red':
            player.invincible = true;
            player.invincibleTimer = 180;
            soundManager.playPowerUp();
            spawnParticles(itemX + 12, item.y + 12, '#ef4444', 20, 10);
            break;
          case 'sanwa-purple':
            player.maxJumps = 3;
            setTimeout(() => { player.maxJumps = 2; }, 10000);
            soundManager.playPowerUp();
            spawnParticles(itemX + 12, item.y + 12, '#8b5cf6', 20, 10);
            break;
          case 'sanwa-blue':
            player.slowMotion = true;
            player.slowMotionTimer = 180;
            soundManager.playPowerUp();
            spawnParticles(itemX + 12, item.y + 12, '#3b82f6', 20, 10);
            break;
          case 'usb-cable':
            // Find nearest platform ahead for grapple target
            const nearestPlatform = platformsRef.current
              .filter(p => p.x - scrollOffsetRef.current > player.x + 100)
              .sort((a, b) => a.x - b.x)[0];
            if (nearestPlatform) {
              player.grappling = true;
              player.grappleTarget = {
                x: nearestPlatform.x - scrollOffsetRef.current + nearestPlatform.width / 2,
                y: nearestPlatform.y - 20
              };
            }
            spawnParticles(itemX + 12, item.y + 12, '#10b981', 20, 10);
            break;
          default:
            scoreRef.current += 15;
            setScore(scoreRef.current);
            spawnParticles(itemX + 12, item.y + 12, '#fbbf24', 15, 6);
        }

        // Check for discount code unlock
        if (scoreRef.current >= 1000 && !showDiscountCode) {
          setShowDiscountCode(true);
        }
      }
    });

    // Enemy collision
    enemiesRef.current.forEach((enemy, index) => {
      enemy.y += enemy.velocityY * timeScale;
      const enemyX = enemy.x - scrollOffsetRef.current;

      if (
        !player.invincible &&
        player.x + player.width > enemyX &&
        player.x < enemyX + 20 &&
        player.y + player.height > enemy.y &&
        player.y < enemy.y + 20
      ) {
        // Hit by enemy - game over
        setGameState('gameover');
        soundManager.playGameOver();
        if (scoreRef.current > highScore) {
          setHighScore(scoreRef.current);
          localStorage.setItem('arkoddRunnerHighScore', scoreRef.current.toString());
        }
        return;
      }

      // Remove off-screen enemies
      if (enemy.y > CANVAS_HEIGHT) {
        enemiesRef.current.splice(index, 1);
      }
    });

    // Update particles
    particlesRef.current = particlesRef.current.filter(p => {
      p.x += p.vx * timeScale;
      p.y += p.vy * timeScale;
      p.vy += 0.1 * timeScale;
      p.life -= timeScale;
      return p.life > 0;
    });

    // Scroll world
    scrollOffsetRef.current += gameSpeedRef.current * timeScale;

    // Increase difficulty
    if (frameCountRef.current % 300 === 0) {
      gameSpeedRef.current += 0.15;
    }

    // Score based on distance
    if (frameCountRef.current % 10 === 0) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
    }

    // Generate new platforms
    const lastPlatform = platformsRef.current[platformsRef.current.length - 1];
    if (lastPlatform && lastPlatform.x - scrollOffsetRef.current < CANVAS_WIDTH + 200) {
      const types: Platform['type'][] = ['padbox', 'hing3d', 'gen5x', 'basic'];
      const gap = 120 + Math.random() * 120 + Math.min(frameCountRef.current / 200, 80);
      const isMoving = Math.random() > 0.75;
      const newY = 350 + Math.random() * 180;

      const newPlatform: Platform = {
        x: lastPlatform.x + gap,
        y: newY,
        width: 70 + Math.random() * 50,
        type: types[Math.floor(Math.random() * types.length)],
        moving: isMoving,
        moveDirection: 1,
        moveRange: 40 + Math.random() * 30,
        originalX: newY
      };
      platformsRef.current.push(newPlatform);

      // Add collectible
      if (Math.random() > 0.4) {
        const collectibleTypes: Collectible['type'][] = [
          'sanwa-yellow', 'sanwa-yellow', 'sanwa-yellow', // Common
          'sanwa-red', 'sanwa-purple', 'sanwa-blue', // Power-ups
          'usb-cable' // Rare
        ];
        const type = collectibleTypes[Math.floor(Math.random() * collectibleTypes.length)];
        collectiblesRef.current.push({
          x: newPlatform.x + newPlatform.width / 2 - 10,
          y: newPlatform.y - 50 - Math.random() * 30,
          collected: false,
          type
        });
      }

      // Spawn enemy
      if (frameCountRef.current > 300 && Math.random() > 0.85) {
        enemiesRef.current.push({
          x: newPlatform.x + newPlatform.width / 2,
          y: -50,
          velocityY: 2 + Math.random() * 2,
          type: Math.random() > 0.5 ? 'cherry-mx' : 'gateron'
        });
      }
    }

    // Cleanup off-screen objects
    platformsRef.current = platformsRef.current.filter(p => p.x - scrollOffsetRef.current > -200);
    collectiblesRef.current = collectiblesRef.current.filter(k => k.x - scrollOffsetRef.current > -100);

    // Draw everything
    platformsRef.current.forEach(platform => {
      drawPlatform(ctx, {
        ...platform,
        x: platform.x - scrollOffsetRef.current
      });
    });

    collectiblesRef.current.forEach(item => {
      drawCollectible(ctx, {
        ...item,
        x: item.x - scrollOffsetRef.current
      });
    });

    enemiesRef.current.forEach(enemy => {
      drawEnemy(ctx, {
        ...enemy,
        x: enemy.x - scrollOffsetRef.current
      });
    });

    drawParticles(ctx);
    drawPlayer(ctx, player);

    // Game over check
    if (player.y > CANVAS_HEIGHT) {
      setGameState('gameover');
      soundManager.playGameOver();
      screenShakeRef.current = 20;
      if (scoreRef.current > highScore) {
        setHighScore(scoreRef.current);
        localStorage.setItem('arkoddRunnerHighScore', scoreRef.current.toString());
      }
      ctx.restore();
      return;
    }

    // Draw UI
    ctx.fillStyle = '#8b5cf6';
    ctx.font = 'bold 24px monospace';
    ctx.fillText(`SCORE: ${scoreRef.current}`, 15, 35);

    ctx.fillStyle = '#fbbf24';
    ctx.font = 'bold 18px monospace';
    ctx.fillText(`HIGH: ${highScore}`, 15, 60);

    ctx.fillStyle = '#3b82f6';
    ctx.fillText(`SPEED: ${gameSpeedRef.current.toFixed(1)}x`, 15, 85);

    // Combo display
    if (comboRef.current >= 3) {
      ctx.fillStyle = '#fbbf24';
      ctx.font = 'bold 28px monospace';
      ctx.fillText(`${comboRef.current}x COMBO!`, CANVAS_WIDTH / 2 - 80, 50);
    }

    // Power-up indicators
    let indicatorY = 35;
    if (player.invincible) {
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 14px monospace';
      ctx.fillText(`INVINCIBLE: ${Math.ceil(player.invincibleTimer / 60)}s`, CANVAS_WIDTH - 150, indicatorY);
      indicatorY += 20;
    }
    if (player.slowMotion) {
      ctx.fillStyle = '#3b82f6';
      ctx.font = 'bold 14px monospace';
      ctx.fillText(`SLOW-MO: ${Math.ceil(player.slowMotionTimer / 60)}s`, CANVAS_WIDTH - 150, indicatorY);
      indicatorY += 20;
    }
    if (player.maxJumps > 2) {
      ctx.fillStyle = '#8b5cf6';
      ctx.font = 'bold 14px monospace';
      ctx.fillText('TRIPLE JUMP!', CANVAS_WIDTH - 150, indicatorY);
    }

    // Draw CRT effect
    drawCRTEffect(ctx);

    ctx.restore();

    gameLoopRef.current = requestAnimationFrame(gameLoop);
  }, [highScore, showDiscountCode]);

  // =============================================================================
  // CONTROLS
  // =============================================================================

  const handleJump = useCallback(() => {
    const player = playerRef.current;
    if (player.jumpCount < player.maxJumps) {
      player.velocityY = JUMP_STRENGTH;
      player.isJumping = true;
      player.jumpCount++;
      player.grappling = false;
      player.grappleTarget = null;
      soundManager.playJump();
      spawnParticles(player.x + player.width / 2, player.y + player.height, '#8b5cf6', 8, 4);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        onClose();
        return;
      }

      if (gameState === 'menu' && (e.code === 'Space' || e.code === 'Enter')) {
        setGameState('playing');
        initGame();
        return;
      }

      if (gameState === 'gameover' && (e.code === 'Space' || e.code === 'Enter')) {
        setGameState('playing');
        initGame();
        return;
      }

      if (gameState === 'playing') {
        if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
          e.preventDefault();
          handleJump();
        }
        if (e.code === 'KeyM') {
          setSoundEnabled(soundManager.toggle());
        }
        keysPressed.current.add(e.code);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keysPressed.current.delete(e.code);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState, handleJump, initGame, onClose]);

  useEffect(() => {
    if (gameState === 'playing') {
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    } else {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    }

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameState, gameLoop]);

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="border-4 border-purple-500 rounded-lg shadow-2xl shadow-purple-500/30"
          style={{ imageRendering: 'pixelated' }}
        />

        {gameState === 'menu' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 rounded-lg">
            <div className="text-center">
              <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 mb-2 font-mono tracking-wider">
                PADBOX RUNNER
              </h1>
              <p className="text-xl text-purple-300 mb-8 font-mono">
                Jump across Arkodd fightsticks!
              </p>

              <div className="text-purple-200 font-mono text-sm mb-8 space-y-2">
                <p>⬆️ SPACE / W - Jump (Double jump enabled!)</p>
                <p>⬅️ ➡️ A/D - Move</p>
                <p>🔇 M - Toggle sound</p>
                <p>❌ ESC - Exit</p>
              </div>

              <div className="mb-8 text-left text-sm font-mono space-y-1">
                <p className="text-red-400">🔴 Red Button = Invincibility</p>
                <p className="text-purple-400">🟣 Purple Button = Triple Jump</p>
                <p className="text-blue-400">🔵 Blue Button = Slow Motion</p>
                <p className="text-green-400">🔌 USB Cable = Grapple Hook</p>
                <p className="text-yellow-400">🟡 Yellow Button = +15 Points</p>
              </div>

              <p className="text-2xl text-purple-400 font-mono animate-pulse mb-4">
                PRESS SPACE TO START
              </p>

              <p className="text-lg text-purple-500 font-mono">
                🏆 High Score: {highScore}
              </p>
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 rounded-lg">
            <h1 className="text-6xl font-bold text-red-500 mb-4 font-mono animate-pulse">
              GAME OVER
            </h1>

            <p className="text-4xl text-purple-400 mb-2 font-mono">
              Score: {score}
            </p>

            <p className="text-2xl text-purple-300 mb-6 font-mono">
              🏆 High Score: {highScore}
            </p>

            {showDiscountCode && (
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 rounded-lg mb-6 animate-bounce">
                <p className="text-white font-mono text-lg">🎉 YOU UNLOCKED A SECRET!</p>
                <p className="text-yellow-300 font-mono text-2xl font-bold">{DISCOUNT_CODE}</p>
                <p className="text-white font-mono text-sm">10% off your next order!</p>
              </div>
            )}

            <p className="text-2xl text-purple-400 font-mono animate-pulse mb-4">
              PRESS SPACE TO RETRY
            </p>

            <p className="text-lg text-purple-500 font-mono">
              ESC to exit
            </p>
          </div>
        )}

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-purple-400 hover:text-purple-300 text-3xl font-bold transition-colors"
          aria-label="Close game"
        >
          ✕
        </button>

        <button
          onClick={() => setSoundEnabled(soundManager.toggle())}
          className="absolute top-4 left-4 text-purple-400 hover:text-purple-300 text-2xl font-bold transition-colors"
          aria-label="Toggle sound"
        >
          {soundEnabled ? '🔊' : '🔇'}
        </button>
      </div>
    </div>
  );
};
